import { useEffect, useRef, useState } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";

// Video posts are converted synchronously on the server and the browser gets no
// progress events for that step, so the bar is driven by time: it approaches
// MAX_SIMULATED_PROGRESS along an exponential curve and only reaches 100% when
// the request actually finishes.
const MAX_SIMULATED_PROGRESS = 95;

// Rough estimate from the rehearsal uploads. It only sets how fast the curve
// rises: a wrong estimate means the bar jumps to 100% from 60% or from 93%.
const SECONDS_PER_MB = 2;
const MIN_EXPECTED_SECONDS = 15;
const MAX_EXPECTED_SECONDS = 300;

// At the expected time the curve is at about 87% of the maximum.
const CURVE_STEEPNESS = 2.5;

const TICK_MS = 500;

const STAGES = [
    { from: 0, message: "Subindo seu post..." },
    { from: 15, message: "Processando vídeo..." },
    { from: 60, message: "Otimizando para reprodução..." },
    { from: 85, message: "Finalizando..." },
];

const getExpectedSeconds = (fileSizeBytes) => {
    const sizeInMb = fileSizeBytes / (1024 * 1024);

    return Math.min(Math.max(sizeInMb * SECONDS_PER_MB, MIN_EXPECTED_SECONDS), MAX_EXPECTED_SECONDS);
};

const getStageMessage = (progress) => STAGES.filter((stage) => progress >= stage.from).pop().message;

export const UploadProgress = ({ fileSize = 0, completed = false }) => {
    const startedAt = useRef(Date.now());
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setElapsedSeconds((Date.now() - startedAt.current) / 1000);
        }, TICK_MS);

        // Closing or reloading the page drops the response, so the user would
        // not know whether the post was published and might send it again.
        const warnBeforeLeaving = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        window.addEventListener("beforeunload", warnBeforeLeaving);

        return () => {
            clearInterval(interval);
            window.removeEventListener("beforeunload", warnBeforeLeaving);
        };
    }, []);

    const timeConstant = getExpectedSeconds(fileSize) / CURVE_STEEPNESS;
    const progress = completed
        ? 100
        : MAX_SIMULATED_PROGRESS * (1 - Math.exp(-elapsedSeconds / timeConstant));

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 360, textAlign: "center" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    {completed ? "Post publicado!" : getStageMessage(progress)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Isso pode levar alguns minutos. Não feche nem atualize esta página.
                </Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
        </Box>
    );
};
